import {
  getConversationAdvancedFilterDefaults,
  getConversationQuickFilterDefaults,
} from "../../../utils/conversationFilterModel";

// The level belongs to the report; sections only inherit it.
export const reportCountField = (level) => level === "calls" ? "id" : "customer_id";

export const isAgentFeedbackDrilldownScore = (value) => value !== null && value !== undefined &&
  String(value).trim() !== "" && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100;

const CUSTOMER_DATE_KEYS = [
  "firstinteraction", "lastinteraction", "first_interaction_time", "last_interaction_time",
];

export const conversationReportFilters = (filters = {}) => {
  const result = { ...filters };
  // Also repair values saved by the report builder before it shared the call-page options.
  const normalizeRecordType = (value) => typeof value === "string" && /^(call|chat)$/i.test(value.trim())
    ? value.trim().toLowerCase() : value;
  if (result.record_type !== undefined) {
    result.record_type = Array.isArray(result.record_type)
      ? result.record_type.map(normalizeRecordType) : normalizeRecordType(result.record_type);
  }
  Object.keys(result).forEach((key) => {
    if (CUSTOMER_DATE_KEYS.some((prefix) => key.startsWith(prefix)) ||
        ["interactions", "customerPotential", "customer_potential"].includes(key)) delete result[key];
  });
  return result;
};

export const getConversationReportDefaults = () => {
  const quickFilters = getConversationQuickFilterDefaults();
  return {
    ...getConversationAdvancedFilterDefaults(),
    call_time: quickFilters.time,
    call_time_start: quickFilters.customStart,
    call_time_end: quickFilters.customEnd,
    call_time_range_type: quickFilters.customRangeType,
    employee_id: quickFilters.employee,
    team_name: quickFilters.team_name,
    conversation_id: quickFilters.conversationId,
    record_type: quickFilters.record_type,
  };
};

const CONVERSATION_REPORT_QUICK_FILTER_KEYS = [
  "call_time", "call_time_start", "call_time_end", "call_time_range_type",
  "employee_id", "team_name", "conversation_id", "record_type",
];

export const preserveConversationReportQuickFilters = (current = {}, next = {}) => {
  const result = { ...next };
  CONVERSATION_REPORT_QUICK_FILTER_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(current, key)) result[key] = current[key];
  });
  return result;
};

const CALL_FILTER_ALIASES = {
  type: "conversation_type", conversationPotential: "conversation_potential",
  conversationOutcome: "customer_outcome", customerId: "customer_id",
  campaignId: "campaign_id", campaignBatchId: "campaign_batch_id",
  actorType: "actor_type", needsReview: "needs_review", openConcerns: "open_concerns",
  refusalCategory: "refusal_category", concernCategory: "concern_category",
  concernSubcategory: "concern_subcategory", queryCategory: "query_category",
  querySubcategory: "query_subcategory",
};

// API-created reports need the same editable state as reports saved in the builder.
export const conversationReportFiltersFromPayload = (payload = {}) => {
  const result = {};
  const aliases = Object.fromEntries(Object.entries(CALL_FILTER_ALIASES).map(([ui, api]) => [api, ui]));
  for (const condition of payload.filter?.and || []) {
    const { field, operator, value, module } = condition || {};
    if (!field || value === undefined || value === null || operator === "neq") continue;
    if (["business_insight", "business_insights", "custom_module", "agent_feedback"].includes(module)) {
      const group = module === "custom_module" ? "custom_module_multiple"
        : module === "agent_feedback" ? "agent_feedback_multiple" : "business_insights_multiple";
      const entry = module === "custom_module"
        ? { custom_module_parameter_name: field, custom_module_parameter_answer: value }
        : module === "agent_feedback"
          ? { agent_feedback_parameter: field, agent_feedback_parameter_score_lower: value.lower ?? value.start,
              agent_feedback_parameter_score_upper: value.upper ?? value.end }
          : { business_insights_parameter: field, business_insights_parameter_value: value };
      result[group] = [...(result[group] || []), entry];
    } else if (["call_time", "crm_lead_created_at"].includes(field)) {
      if (operator === "date_preset") result[field] = value;
      const isAfter = ["after", "gte", "gt"].includes(operator);
      if (operator === "between" || isAfter) {
        result[field] = "Custom Range";
        result[`${field}_range_type`] = isAfter ? "after" : "between";
        result[`${field}_start`] = isAfter ? value : value.lower ?? value.start ?? "";
        result[`${field}_end`] = isAfter ? "" : value.upper ?? value.end ?? "";
      }
    } else if (field === "audio_duration") {
      if (operator === "between") {
        result.audioDuration = "Custom Range";
        result.audioDurationLower = value.lower ?? value.start ?? null;
        result.audioDurationUpper = value.upper ?? value.end ?? null;
      } else result.audioDuration = value;
    } else if (field === "overall_score" && operator === "between") {
      result.scoreLower = value.lower ?? value.start ?? null;
      result.scoreUpper = value.upper ?? value.end ?? null;
    } else if (operator === "eq" || operator === "in") {
      result[aliases[field] || field] = value;
    }
  }
  return result;
};

export const conversationReportApiFilters = (filters = {}) => {
  const result = conversationReportFilters(filters);
  Object.entries(CALL_FILTER_ALIASES).forEach(([uiKey, apiKey]) => {
    if (Object.prototype.hasOwnProperty.call(result, uiKey)) {
      result[apiKey] = result[uiKey];
      delete result[uiKey];
    }
  });
  if (result.audioDuration === "Custom Range") {
    result.module_parameter_filters = [
      ...(result.module_parameter_filters || []),
      { field: "audio_duration", operator: "between", value: {
        lower: result.audioDurationLower === "" ? null : result.audioDurationLower,
        upper: result.audioDurationUpper === "" ? null : result.audioDurationUpper,
      } },
    ];
    delete result.audioDuration;
  }
  delete result.audioDurationLower;
  delete result.audioDurationUpper;
  return result;
};

export const conversationReportDrilldownFilters = (filters = {}) => {
  const result = conversationReportFilters(filters);
  Object.entries(CALL_FILTER_ALIASES).forEach(([uiKey, apiKey]) => {
    if (result[apiKey] !== undefined) result[uiKey] = result[apiKey];
  });
  if (result.lead_stage !== undefined) result.crm_lead_stage = result.lead_stage;
  if (result.lead_sub_stage !== undefined) result.crm_lead_sub_stage = result.lead_sub_stage;
  return {
    ...result,
    employee: result.employee_id || result.employee || "",
    // Pie drilldowns use the physical id column; it must override a saved ID filter.
    conversationId: result.id || result.conversation_id || "",
    time: result.call_time || "Last 7 Days",
    customStart: result.call_time_start || "",
    customEnd: result.call_time_end || "",
    customRangeType: result.call_time_range_type || "between",
  };
};

export const isConversationCountDrilldown = (visualization, reportLevel) =>
  (reportLevel || visualization?.drilldown?.type) === "calls" &&
  !(visualization?.measures || visualization?.config?.measures || []).length &&
  (visualization?.aggregationOperator || visualization?.config?.aggregationOperator || "count") === "count";
